import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { MapPin } from 'lucide-react';
import { toast } from '../../../ui/toast';
import { DIALOG_TYPE } from '../../../constants/DialogTypes';
import { Dialog } from '../../../ui/components/Common/Dialog/Dialog';
import { addToInviteList } from '../../../utils/api_service/JobOpenings';
import { getJobPostList } from '../../../utils/api_service/JobPosting';
import { JobPostingData } from '../../../utils/types/JobOpenings';

interface AddToInviteListModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidateIds: string[];
  candidateName?: string;
  /** Names of selected candidates for display in bulk mode */
  candidateNames?: string[];
}

const selectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 10050 }),
  menu: (base: any) => ({
    ...base,
    zIndex: 10050,
    borderRadius: '0.5rem',
    boxShadow:
      '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
    border: '1px solid #e2e8f0',
  }),
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '8px',
    border: `1px solid ${state.isFocused ? '#6366f1' : '#e2e8f0'}`,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(99,102,241,0.12)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#6366f1' : '#cbd5e1' },
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '13px',
    padding: '8px 12px',
    backgroundColor: state.isSelected
      ? '#6366f1'
      : state.isFocused
        ? '#f1f5f9'
        : 'white',
    color: state.isSelected ? 'white' : '#334155',
  }),
  placeholder: (base: any) => ({ ...base, color: '#94a3b8', fontSize: '13px' }),
  singleValue: (base: any) => ({ ...base, fontSize: '13px', color: '#334155' }),
};

export const AddToInviteListModal: React.FC<AddToInviteListModalProps> = ({
  isOpen,
  onClose,
  candidateIds,
  candidateName,
  candidateNames,
}) => {
  const [jobOpenings, setJobOpenings] = useState<JobPostingData[]>([]);
  const [selectedJob, setSelectedJob] =
    useState<{ value: string; label: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedJob(null);
    setIsLoading(true);

    getJobPostList([], [], [], [], 1, 12)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        const list = data.results ?? data.data ?? (Array.isArray(data) ? data : []);
        setJobOpenings(list);
      })
      .catch(() =>
        toast.error('Failed to load job openings', {
          position: 'bottom-center',
        }),
      )
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  const jobOptions = useMemo(() => {
    const seenIds = new Set<string>();
    const options: {
      value: string;
      label: string;
      location: string;
      referenceNumber: string;
    }[] = [];

    for (const j of jobOpenings as any[]) {
      if (!j) continue;
      // Filter active job openings (must not be explicitly inactive or closed)
      if (
        j.active === false ||
        j.status === 'closed' ||
        j.status === 'Inactive' ||
        j.status === 'Closed'
      ) {
        continue;
      }

      const val = j.jobOpeningId || j.id || j.job_opening_id || j.job_opening;
      if (!val || seenIds.has(val)) continue;
      seenIds.add(val);

      const title =
        j.jobTitle || j.job_title || j.title || j.name || 'Untitled Role';
      const loc =
        j.location ||
        (j.contactInfo?.address?.city ? j.contactInfo.address.city : '');
      const refNum =
        j.jobReferenceNumber ||
        j.job_reference_number ||
        j.referenceNumber ||
        '';

      options.push({
        value: val,
        label: title,
        location: loc,
        referenceNumber: refNum,
      });
    }

    return options;
  }, [jobOpenings]);

  const formatJobOption = (opt: {
    label: string;
    location: string;
    referenceNumber: string;
  }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant='body2' fontWeight={600} color='text.primary'>
        {opt.label}
      </Typography>
      <Stack direction='row' spacing={1.5} alignItems='center'>
        {opt.location && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MapPin size={12} color='#94a3b8' />
            <Typography variant='caption' color='text.secondary'>
              {opt.location}
            </Typography>
          </Box>
        )}
        {opt.referenceNumber && (
          <Typography variant='caption' fontWeight={500} color='primary.main'>
            Ref: {opt.referenceNumber}
          </Typography>
        )}
      </Stack>
    </Box>
  );

  const handleConfirm = async () => {
    if (!selectedJob || candidateIds.length === 0) return;
    setIsSubmitting(true);
    try {
      const response = await addToInviteList(selectedJob.value, {
        jobApplicantIds: candidateIds,
      });
      if (response.ok) {
        const label =
          candidateName ??
          `${candidateIds.length} candidate${
            candidateIds.length !== 1 ? 's' : ''
          }`;
        toast.success(`Added ${label} to invite list`, {
          position: 'bottom-center',
        });
        onClose();
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail ?? 'Failed to add to invite list', {
          position: 'bottom-center',
        });
      }
    } catch {
      toast.error('Error adding to invite list', { position: 'bottom-center' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSingle = !!candidateName;
  const count = candidateIds.length;
  const title = isSingle
    ? 'Add to Invite List'
    : `Add ${count} Candidate${count !== 1 ? 's' : ''} to Invite List`;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      handleApply={handleConfirm}
      title={title}
      confirmButtonLable={isSubmitting ? 'Adding...' : 'Add to Invite List'}
      cancelButtonLable='Cancel'
      dialogType={DIALOG_TYPE.SUCCESS}
      maxWidth='480px'
      disabled={!selectedJob || isSubmitting}
    >
      <Stack spacing={2.5} sx={{ py: 1 }}>
        {/* Candidate display */}
        {isSingle ? (
          <Typography variant='body2' color='text.secondary'>
            Are you sure you want to add{' '}
            <Typography component='span' variant='body2' fontWeight={700} color='text.primary'>
              {candidateName}
            </Typography>{' '}
            to the invite list?
          </Typography>
        ) : (
          <Stack spacing={1}>
            <Typography variant='body2' color='text.secondary'>
              Are you sure you want to add{' '}
              <Typography component='span' variant='body2' fontWeight={700} color='text.primary'>
                {count} candidate{count !== 1 ? 's' : ''}
              </Typography>{' '}
              to the invite list?
            </Typography>
            {candidateNames && candidateNames.length > 0 && (
              <Box
                sx={{
                  maxHeight: 112,
                  overflowY: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  p: 1.25,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {candidateNames.slice(0, 20).map((name, i) => (
                  <Chip
                    key={i}
                    label={name}
                    size='small'
                    variant='outlined'
                    sx={{
                      bgcolor: '#ffffff',
                      fontSize: '0.75rem',
                      height: 24,
                    }}
                  />
                ))}
                {candidateNames.length > 20 && (
                  <Typography variant='caption' color='text.disabled' sx={{ alignSelf: 'center', px: 0.5 }}>
                    +{candidateNames.length - 20} more
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}

        {/* Job search dropdown */}
        <Stack spacing={0.75}>
          <Typography variant='caption' fontWeight={700} color='text.secondary'>
            Select Job Opening
          </Typography>
          <Select
            options={jobOptions}
            value={selectedJob}
            onChange={(opt: any) => setSelectedJob(opt)}
            isLoading={isLoading}
            isDisabled={isLoading}
            placeholder='Search and select a job...'
            menuPortalTarget={document.body}
            styles={selectStyles}
            formatOptionLabel={formatJobOption}
            noOptionsMessage={() => 'No job openings found'}
            loadingMessage={() => 'Loading...'}
          />
        </Stack>
      </Stack>
    </Dialog>
  );
};
